/**
 * Google Maps ルート可視化コンポーネント
 * FL-170: Directions API との連携によるルート地図表示
 *
 * 責務:
 * - Google Maps Static API を使用したルート地図表示
 * - ルート情報（距離、所要時間）の表示
 * - トラフィック情報の色分け表示
 * - ダークモード対応
 */

import React, { useMemo } from 'react';
import type { RouteVisualizationData } from '@/types/maps';

export interface RouteVisualizationComponentProps {
  /** ルート可視化データ */
  routeData: RouteVisualizationData | null;

  /** カスタムクラス名 */
  className?: string;

  /** ルート詳細情報を表示するか */
  showDetails?: boolean;

  /** 地図の高さ（px） */
  mapHeight?: number;

  /** ローディング状態 */
  isLoading?: boolean;

  /** リフレッシュボタンのコールバック（表示される場合） */
  onRefresh?: () => void;

  /** リフレッシュボタンのローディング状態 */
  isRefreshing?: boolean;
}

/**
 * Google Maps Static API を使用したルート地図表示コンポーネント
 *
 * 機能:
 * - Google Maps Static API でルート地図を画像として取得
 * - ポリラインで実際のルートを表示
 * - 開始地点（緑）と終了地点（赤）のマーカー表示
 * - トラフィック情報に応じた見出し色分け
 * - ダークモード完全対応
 *
 * @example
 * <RouteVisualizationComponent
 *   routeData={routeData}
 *   showDetails={true}
 *   mapHeight={400}
 * />
 */
export const RouteVisualizationComponent = React.forwardRef<
  HTMLDivElement,
  RouteVisualizationComponentProps
>(
  (
    {
      routeData,
      className = '',
      showDetails = true,
      mapHeight = 400,
      isLoading = false,
      onRefresh,
      isRefreshing = false,
    },
    ref
  ) => {
    // Google Maps Static API URL を生成
    const mapUrl = useMemo(() => {
      if (!routeData || !routeData.polyline_points || routeData.polyline_points.length === 0) {
        return null;
      }

      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        return null;
      }

      // ポリラインポイントを Google Maps Static API 形式に変換
      const polylinePoints = routeData.polyline_points
        .map(point => `${point.lat},${point.lng}`)
        .join('|');

      // 開始・終了マーカーの座標
      const startPoint = routeData.polyline_points[0];
      const endPoint = routeData.polyline_points[routeData.polyline_points.length - 1];

      // URL パラメータ構築
      const params = new URLSearchParams({
        size: '600x400',
        scale: '2',
        zoom: '10',
        key: apiKey,
        // ポリライン（ルート線）
        path: `color:0x0066FF|weight:3|${polylinePoints}`,
        // 開始マーカー（緑）
        markers: `color:green|label:S|${startPoint.lat},${startPoint.lng}`,
        // 終了マーカー（赤）：複数 markers パラメータは URL に複数追加
      });

      // 終了マーカーを追加（URL に markers を2回追加）
      const url = `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}&markers=color:red|label:E|${endPoint.lat},${endPoint.lng}`;

      return url;
    }, [routeData]);

    // トラフィック レベルに応じた色を決定
    const trafficColor = useMemo(() => {
      if (!routeData?.traffic_level) return 'text-gray-700 dark:text-gray-300';

      switch (routeData.traffic_level) {
        case 'light':
          return 'text-green-600 dark:text-green-400';
        case 'moderate':
          return 'text-yellow-600 dark:text-yellow-400';
        case 'heavy':
          return 'text-red-600 dark:text-red-400';
        default:
          return 'text-gray-700 dark:text-gray-300';
      }
    }, [routeData?.traffic_level]);

    // トラフィック レベルのテキスト表示
    const trafficText = useMemo(() => {
      if (!routeData?.traffic_level) return '情報なし';

      switch (routeData.traffic_level) {
        case 'light':
          return '渋滞なし';
        case 'moderate':
          return '中程度の渋滞';
        case 'heavy':
          return '渋滞あり';
        default:
          return '情報なし';
      }
    }, [routeData?.traffic_level]);

    // データソースバッジの表示テキストと色を決定
    const dataSourceDisplay = useMemo(() => {
      if (!routeData?.distance_source) {
        return {
          text: '計算中',
          bgColor: 'bg-gray-100 dark:bg-gray-700',
          textColor: 'text-gray-700 dark:text-gray-300',
        };
      }

      switch (routeData.distance_source) {
        case 'cache':
          return {
            text: 'キャッシュ',
            bgColor: 'bg-green-100 dark:bg-green-900',
            textColor: 'text-green-700 dark:text-green-300',
          };
        case 'api':
          return {
            text: 'API取得',
            bgColor: 'bg-blue-100 dark:bg-blue-900',
            textColor: 'text-blue-700 dark:text-blue-300',
          };
        case 'expired-cache':
          return {
            text: '期限切れ',
            bgColor: 'bg-amber-100 dark:bg-amber-900',
            textColor: 'text-amber-700 dark:text-amber-300',
          };
        default:
          return {
            text: '不明',
            bgColor: 'bg-gray-100 dark:bg-gray-700',
            textColor: 'text-gray-700 dark:text-gray-300',
          };
      }
    }, [routeData?.distance_source]);

    // ルート情報がない場合
    if (!routeData) {
      return (
        <div
          ref={ref}
          className={`rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-6 text-center ${className}`}
        >
          <p className='text-sm text-gray-500 dark:text-gray-400'>
            {isLoading ? 'ルート計算中...' : 'ルート情報が利用できません'}
          </p>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={`rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800 ${className}`}
      >
        {/* 地図コンテナ */}
        {mapUrl && (
          <div
            className='relative bg-gray-100 dark:bg-gray-900'
            style={{ height: `${mapHeight}px` }}
          >
            <img
              src={mapUrl}
              alt='ルート地図'
              className='w-full h-full object-cover'
              loading='lazy'
            />

            {/* ローディング表示 */}
            {isLoading && (
              <div className='absolute inset-0 flex items-center justify-center bg-black/30'>
                <div className='bg-white dark:bg-gray-800 rounded-full p-4 shadow-lg'>
                  <div className='h-8 w-8 animate-spin rounded-full border-4 border-gray-300 dark:border-gray-600 border-t-blue-500'></div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ルート情報セクション */}
        {showDetails && (
          <div className='p-4 space-y-3'>
            {/* 距離と所要時間 */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='rounded-md bg-blue-50 dark:bg-blue-900/20 p-3'>
                <div className='flex items-center justify-between mb-2'>
                  <p className='text-xs text-gray-600 dark:text-gray-400'>走行距離</p>
                  <span
                    className={`inline-block px-2 py-1 rounded text-xs font-medium ${dataSourceDisplay.bgColor} ${dataSourceDisplay.textColor}`}
                  >
                    {dataSourceDisplay.text}
                  </span>
                </div>
                <p className='text-lg font-semibold text-blue-700 dark:text-blue-300 mb-2'>
                  {routeData.distance_km} km
                </p>
                {onRefresh && (
                  <button
                    onClick={onRefresh}
                    disabled={isRefreshing}
                    className='w-full px-2 py-1 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/50 hover:bg-blue-200 dark:hover:bg-blue-900 text-blue-700 dark:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    {isRefreshing ? '更新中...' : '再計算'}
                  </button>
                )}
              </div>

              <div className='rounded-md bg-purple-50 dark:bg-purple-900/20 p-3'>
                <p className='text-xs text-gray-600 dark:text-gray-400 mb-1'>所要時間</p>
                <p className='text-lg font-semibold text-purple-700 dark:text-purple-300'>
                  {routeData.estimated_duration_minutes} 分
                </p>
              </div>
            </div>

            {/* 期限切れキャッシュの免責テキスト */}
            {routeData.distance_source === 'expired-cache' && (
              <div className='rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3'>
                <p className='text-xs text-amber-700 dark:text-amber-300 leading-relaxed'>
                  ⚠️ 表示データの精度が低い可能性があります。
                  <br />
                  実際の距離・所要時間と異なる場合があります。
                  <br />
                  『再計算』ボタンで最新情報を取得してください。
                </p>
              </div>
            )}

            {/* トラフィック情報 */}
            {routeData.duration_in_traffic_minutes && (
              <div className='rounded-md bg-gray-50 dark:bg-gray-700/30 p-3 border border-gray-200 dark:border-gray-700'>
                <div className='flex items-center justify-between'>
                  <p className='text-xs text-gray-600 dark:text-gray-400'>トラフィック考慮</p>
                  <p className={`text-sm font-semibold ${trafficColor}`}>{trafficText}</p>
                </div>
                <p className='text-sm font-semibold text-gray-900 dark:text-white mt-2'>
                  {routeData.duration_in_traffic_minutes} 分
                </p>
              </div>
            )}

            {/* 住所情報 */}
            <div className='border-t border-gray-200 dark:border-gray-700 pt-3 space-y-2'>
              <div>
                <p className='text-xs text-gray-600 dark:text-gray-400 mb-1'>引取先</p>
                <p className='text-sm text-gray-900 dark:text-white truncate'>
                  {routeData.pickup_address}
                </p>
              </div>
              <div>
                <p className='text-xs text-gray-600 dark:text-gray-400 mb-1'>搬入先</p>
                <p className='text-sm text-gray-900 dark:text-white truncate'>
                  {routeData.delivery_address}
                </p>
              </div>
            </div>

            {/* ルートステップ（オプション） */}
            {routeData.steps && routeData.steps.length > 0 && (
              <details className='border-t border-gray-200 dark:border-gray-700 pt-3'>
                <summary className='cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'>
                  ルート詳細（{routeData.steps.length} ステップ）
                </summary>

                <ul className='mt-3 space-y-2 max-h-48 overflow-y-auto'>
                  {routeData.steps.map((step, index) => (
                    <li
                      key={index}
                      className='text-xs text-gray-600 dark:text-gray-400 rounded-md bg-gray-50 dark:bg-gray-700/30 p-2 border border-gray-200 dark:border-gray-700'
                    >
                      <p className='font-medium text-gray-900 dark:text-white mb-1'>
                        ステップ {index + 1}: {step.instruction}
                      </p>
                      <p className='text-gray-600 dark:text-gray-400'>
                        {Math.round(step.distance_meters / 100) / 10} km
                        {' ・ '}
                        {Math.ceil(step.duration_seconds / 60)} 分
                      </p>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    );
  }
);

RouteVisualizationComponent.displayName = 'RouteVisualizationComponent';
